import { useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, Clock, Filter, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNotionInsights } from "@/hooks/useNotionInsights";
import { useLanguage } from "@/contexts/LanguageContext";
import { SEO } from "@/components/SEO";

const Insights = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { data: insights, isLoading, error } = useNotionInsights();
  const { t, locale } = useLanguage();

  const categories = Array.from(
    new Set(insights?.map((i) => i.category).filter(Boolean) || [])
  ).sort();

  const filteredInsights = selectedCategory
    ? insights?.filter((i) => i.category === selectedCategory)
    : insights;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Insights | It's All About Growth"
        description="Strategic essays on B2B revenue systems, GTM, fractional leadership, and operating models for mid-sized service companies."
        path="/insights"
      />
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 lg:py-24 relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-primary/5 rounded-full blur-[100px]" />
          </div>

          <div className="section-container relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl"
            >
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-8">
                <BookOpen className="w-8 h-8 text-primary" />
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                {t("insights.title")}
              </h1>

              <p className="text-xl text-muted-foreground">
                {t("insights.subtitle")}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Category Filters */}
        <section className="py-8 border-y border-border/50 bg-muted/20">
          <div className="section-container">
            <div className="flex items-center gap-3 overflow-x-auto pb-2">
              <Filter className="w-4 h-4 text-muted-foreground shrink-0" />
              <Button
                variant={selectedCategory === null ? "default" : "ghost"}
                size="sm"
                onClick={() => setSelectedCategory(null)}
                className="shrink-0"
              >
                {locale === 'es' ? 'Todos' : 'All'}
              </Button>
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className="shrink-0"
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-16 lg:py-24">
          <div className="section-container">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
                <p className="text-muted-foreground">{t("insights.loading")}</p>
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <p className="text-destructive mb-4">{t("insights.error")}</p>
                <p className="text-muted-foreground text-sm">
                  {locale === 'es' ? 'Por favor intenta de nuevo más tarde' : 'Please try again later'}
                </p>
              </div>
            ) : filteredInsights && filteredInsights.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredInsights.map((insight, index) => (
                  <motion.article
                    key={insight.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                  >
                    <Link
                      to={`/insights/${insight.slug}`}
                      className="group block h-full bg-card border border-border/50 rounded-2xl p-6 hover:border-primary/30 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <Badge
                          variant="secondary"
                          className="bg-primary/10 text-primary hover:bg-primary/20"
                        >
                          {insight.category}
                        </Badge>
                        <span className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock className="w-3.5 h-3.5" />
                          {insight.readTime}
                        </span>
                      </div>

                      <h3 className="font-display text-xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">
                        {insight.title}
                      </h3>

                      <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">
                        {insight.excerpt}
                      </p>
                    </Link>
                  </motion.article>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-muted-foreground">
                  {t("insights.empty")}
                </p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Insights;
